import { ChangeDetectionStrategy, Component, computed, effect, inject, input, untracked } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { BillingAddress } from '../../../models/billing-address';
import { PaymentData } from '../../../models/payment-data';
import { CommonValidators } from '../../../shared/validators/common-validators';
import { OrderStepBaseComponent } from '../../order-step-base-component';
import { OrderState } from 'app/order/models/order-state.model';
import {
    ACCOUNT_HOLDER_REQUIRED,
    ACCOUNT_HOLDER_TOO_SHORT,
    BIC_PATTERN,
    BIC_REQUIRED,
    IBAN_BANK_CODE,
    IBAN_CHECKSUM,
    IBAN_NO_COUNTRY_CODE,
    IBAN_PATTERN,
    IBAN_REQUIRED,
    NAME_OF_BANK_REQUIRED,
    NAME_OF_BANK_TOO_SHORT,
} from '@config/error-messages.const';
import { validateIban } from 'app/shared/validators/iban.validator';
import { NavigationButtons } from '../navigation-buttons/navigation-buttons.component';
import { RequiredInfoComponent } from 'app/shared/components/required-info/required-info.component';
import { CollapsibleCheckboxComponent } from 'app/shared/components/collapsible-checkbox/collapsible-checkbox.component';
import { CtrlErrorMessageDirective } from '@co-3/ctrl-ui/forms';
import { ReactiveLabelDirective } from 'app/shared/directives/reactive-label.directive';
import { toSignal } from '@angular/core/rxjs-interop';
import { map, startWith } from 'rxjs';

@Component({
    selector: 'app-payment-data-form',
    templateUrl: './payment-data-form.component.html',
    imports: [
        NavigationButtons,
        RequiredInfoComponent,
        CollapsibleCheckboxComponent,
        CtrlErrorMessageDirective,
        ReactiveFormsModule,
        ReactiveLabelDirective,
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PaymentDataFormComponent extends OrderStepBaseComponent<Pick<OrderState, 'payment'>> {
    public payment = input<Partial<PaymentData>>();
    public billingAddress = input<Partial<BillingAddress>>();

    public form = inject(FormBuilder).group({
        debit: [false, Validators.requiredTrue],
        iban: ['', [Validators.required, validateIban]],
        bic: [
            null as string | null,
            [Validators.required, Validators.pattern('^[a-zA-Z]{6}[a-zA-Z0-9]{2}([a-zA-Z0-9]{3})?$')],
        ],
        accountHolder: ['', CommonValidators.something],
        nameOfBank: ['', CommonValidators.something],
    });

    protected enableBic = toSignal(
        this.form.controls.iban.valueChanges.pipe(
            startWith(this.form.controls.iban.value),
            map((iban: string) => iban.length >= 2 && !iban.toLocaleLowerCase().startsWith('de')),
        ),
    );

    protected bicLabel = computed(() => (this.enableBic() ? 'BIC*' : 'BIC (nur für Konten außerhalb Deutschlands)'));
    // #region errormessages
    protected ibanErrorMessages = {
        required: IBAN_REQUIRED,
        bankCode: IBAN_BANK_CODE,
        checksum: IBAN_CHECKSUM,
        type: IBAN_REQUIRED,
        bBanPattern: IBAN_PATTERN,
        noCountryPatternFound: IBAN_NO_COUNTRY_CODE,
    };
    protected bicErrorMessages = {
        required: BIC_REQUIRED,
        pattern: BIC_PATTERN,
    };
    protected nameOfBankErrorMessages = {
        required: NAME_OF_BANK_REQUIRED,
        minlength: NAME_OF_BANK_TOO_SHORT,
    };
    protected accountHolderErrorMessages = {
        required: ACCOUNT_HOLDER_REQUIRED,
        minlength: ACCOUNT_HOLDER_TOO_SHORT,
    };
    // #endregion

    public constructor() {
        super();
        effect(() => {
            const payment = this.payment();
            this.form.patchValue({ ...(payment ?? {}), accountHolder: untracked(() => this.getAccountHolder()) });
        });
        effect(() => {
            if (this.enableBic()) {
                this.form.controls.bic.enable();
            } else {
                this.form.controls.bic.disable();
                this.form.controls.bic.reset(null);
            }
        });
    }

    public getData(): Partial<OrderState> {
        return {
            payment: {
                iban: this.form.controls.iban.value,
                bic: this.form.controls.bic.value,
                accountHolder: this.form.controls.accountHolder.value,
                nameOfBank: this.form.controls.nameOfBank.value,
            },
        };
    }

    private getAccountHolder(): string {
        if (this.payment()?.accountHolder) {
            return this.payment().accountHolder;
        } else if (this.billingAddress() && this.billingAddress().firstName && this.billingAddress().lastName) {
            return `${this.billingAddress().firstName} ${this.billingAddress().lastName}`;
        }
        return '';
    }
}
